import { Box } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import type { FC, ReactNode } from 'react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { When } from 'react-if';

import { Alert, type AlertProps } from '@/components/DataDisplay';
import { Caption } from '@/components/Typography';
import { useDebounce } from '@/hooks/useDebounce';

import type { PageAlertValue } from './types';

const PageAlertContext = React.createContext<PageAlertValue>({
  show: () => undefined,
  dismiss: () => undefined,
});

export interface PageAlertProviderProps {
  readonly children: ReactNode;
}

const PageAlertProvider: FC<PageAlertProviderProps> = ({
  children,
}: PageAlertProviderProps) => {
  const debounce = useDebounce();
  const { pathname } = useRouter();
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState<AlertProps['variant']>(undefined);
  const [text, setText] = useState<string | undefined>(undefined);
  const [dataTestId, setDataTestId] = useState<string | undefined>(undefined);
  const { show, dismiss } = useMemo(
    (): Pick<PageAlertValue, 'show' | 'dismiss'> => ({
      show: (t, v, i) => {
        setVariant(v);
        setText(t ?? 'Terjadi kesalahan pada sistem. Coba ulangi lagi');
        setDataTestId(i ?? undefined);
        setVisible(true);

        debounce(() => {
          setVisible(false);
        }, 10000);
      },
      dismiss: () => {
        setVisible(false);
      },
    }),
    [debounce]
  );
  const value = useMemo(
    () => ({
      visible,
      show,
      dismiss,
    }),
    [visible, show, dismiss]
  );

  useEffect(() => {
    setVisible(false);
  }, [pathname]);

  return (
    <PageAlertContext.Provider value={value}>
      {children}
      <When condition={visible}>
        <Box
          data-testid={dataTestId}
          position="fixed"
          width={{ base: '100%', md: 'auto' }}
          top={{ base: 'unset', md: 0 }}
          bottom={{ base: 0, md: 'unset' }}
          right={{ base: 'unset', md: 0 }}
          px={{ base: '24px', md: '32px' }}
          py={{ base: '16px', md: '40px' }}
          display={{ md: 'flex' }}
          justifyContent={{ md: 'flex-end' }}
        >
          <Alert variant={variant} dismiss={dismiss} data-testid="alert">
            <Caption variant="Regular">{text}</Caption>
          </Alert>
        </Box>
      </When>
    </PageAlertContext.Provider>
  );
};

export const usePageAlert = (): PageAlertValue => {
  return useContext(PageAlertContext);
};

export default PageAlertProvider;
